import { useState, useEffect, useCallback } from 'react'
import { CLOUD_API, CLOUD_NOT_CONNECTED, CONTAINER_SHELF_LIFE_MS, deviceBadge } from './shared'
import { cx, hint, table, tableWrap, td, tdNum, th, thNum } from '@ui/classes'
import { errBanner, shiftTab, shiftTabActive, shiftTabInactive } from '@ui/admin'

import { kg } from '@core/utils/format'
import { httpErrorText } from '@core/utils/httpError'
import { Alert, Box, Dot } from '@ui/icons'
interface CloudContainer {
  id: string
  device_id: string | null
  barcode: string
  name: string
  datamatrix: string
  initial_weight: string | number
  remaining_weight: string | number
  status: 'active' | 'exhausted'
  opened_at: string
}

/** Срок годности вскрытой тары — 72 ч (согласовано с кассой). */

export function ContainersMonitorSection({
  token,
  refreshKey = 0
}: {
  token: string
  refreshKey?: number
}) {
  const [containers, setContainers] = useState<CloudContainer[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [tab, setTab] = useState<'active' | 'archive'>('active')

  const reload = useCallback(async () => {
    if (!token) {
      setContainers([])
      setErr(null)
      return
    }
    setErr(null)
    setBusy(true)
    try {
      const resp = await fetch(`${CLOUD_API}/sync/containers`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!resp.ok) {
        setErr(httpErrorText('Загрузка упаковок', resp.status))
        return
      }
      const body = (await resp.json()) as { containers: CloudContainer[] }
      setContainers(Array.isArray(body.containers) ? body.containers : [])
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }, [token])

  useEffect(() => {
    void reload()
  }, [reload, refreshKey])

  const CONTAINER_STATUS = {
    active: 'font-semibold text-okDark',
    exhausted: 'text-muted',
    overdue: 'font-bold text-dangerDark'
  } as const

  const active = containers.filter((c) => c.status === 'active')
  const archive = containers.filter((c) => c.status !== 'active')
  const rows = tab === 'active' ? active : archive

  return (
    <div aria-busy={busy}>
      {err && <p className={errBanner}>{err}</p>}
      {!token && (
        <p className={hint}>{CLOUD_NOT_CONNECTED}. Контроль упаковок доступен после входа.</p>
      )}

      <div className="mb-2.5 flex gap-2">
        <button
          className={tab === 'active' ? cx(shiftTab, shiftTabActive) : cx(shiftTab, shiftTabInactive)}
          onClick={() => setTab('active')}
        >
          <Dot size={16} className="text-okDark" /> Активные ({active.length})
        </button>
        <button
          className={tab === 'archive' ? cx(shiftTab, shiftTabActive) : cx(shiftTab, shiftTabInactive)}
          onClick={() => setTab('archive')}
        >
          <Box size={16} /> Архив ({archive.length})
        </button>
      </div>

      {rows.length === 0 ? (
        <p className={hint}>
          {tab === 'active' ? 'Открытых упаковок нет.' : 'Архив пуст — закрытых упаковок нет.'}
        </p>
      ) : (
        <div className={tableWrap}>
          <table className={table}>
            <thead>
              <tr>
                <th className={th}>Товар</th>
                <th className={thNum}>Остаток / Начальный</th>
                <th className={th}>Вскрыта</th>
                <th className={th}>Касса</th>
                <th className={th}>Статус</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const dev = deviceBadge(c.device_id ?? '—')
                const initial = Number(c.initial_weight)
                const remaining = Number(c.remaining_weight)
                const stale =
                  c.status === 'active' &&
                  Date.now() - new Date(c.opened_at).getTime() > CONTAINER_SHELF_LIFE_MS
                return (
                  <tr key={c.id}>
                    <td className={td}>
                      <div className="font-semibold">{c.name}</div>
                      <div className="text-micro text-muted">{c.barcode}</div>
                    </td>
                    <td className={tdNum}>
                      {kg(remaining)} / {kg(initial)} кг
                    </td>
                    <td className={td}>{new Date(c.opened_at).toLocaleString('ru-RU')}</td>
                    <td className={td}>
                      <div className="font-semibold">{dev.label}</div>
                      <div className="text-micro text-muted">{dev.short}</div>
                    </td>
                    <td className={td}>
                      {c.status === 'active' ? (
                        stale ? (
                          <span className={cx(CONTAINER_STATUS.overdue, 'inline-flex items-center gap-1')}><Alert size={16} /> Просрочена</span>
                        ) : (
                          <span className={cx(CONTAINER_STATUS.active, 'inline-flex items-center gap-1')}><Dot size={16} /> Активна</span>
                        )
                      ) : (
                        <span className={cx(CONTAINER_STATUS.exhausted, 'inline-flex items-center gap-1')}><Box size={16} /> Исчерпана</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

/**
 * Секция реквизитов организации (54-ФЗ) и торговых точек.
 */
